import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem,
  Tooltip, alpha, Avatar, Divider, Skeleton, Tabs, Tab, LinearProgress, Badge,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction, Stepper, Step, StepLabel,
  Paper, Checkbox, Alert, CircularProgress, FormControlLabel
} from '@mui/material';
import {
  Add, Edit, Delete, Search, VerifiedUser, Close, Warning,
  CloudUpload, Description, Visibility, CheckCircle, Cancel, Schedule,
  Business, Person, Assignment, AttachFile,
  Download, Phone, Timeline,
  ContentPaste, Scanner, Preview, PlayArrow, FolderOpen, InsertDriveFile,
  ArrowForward
} from '@mui/icons-material';
import { credentialsAPI, providersAPI, documentsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { fullName } from '../../utils/formatters';
import { useTableSort } from '../../components/useTableSort';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const statusConfig = {
  not_started: { label: 'Not Started', bg: '#f1f5f9', color: '#64748b' },
  gathering_docs: { label: 'Gathering Docs', bg: alpha('#f59e0b', 0.1), color: '#d97706' },
  pending: { label: 'Pending', bg: alpha('#f59e0b', 0.1), color: '#d97706' },
  submitted: { label: 'Submitted', bg: alpha('#3b82f6', 0.1), color: '#2563eb' },
  in_review: { label: 'In Review', bg: alpha('#8b5cf6', 0.1), color: '#7c3aed' },
  follow_up: { label: 'Follow Up', bg: alpha('#ec4899', 0.1), color: '#db2777' },
  approved: { label: 'Approved', bg: alpha('#10b981', 0.1), color: '#059669' },
  active: { label: 'Active', bg: alpha('#10b981', 0.1), color: '#059669' },
  denied: { label: 'Denied', bg: alpha('#ef4444', 0.1), color: '#dc2626' },
  expired: { label: 'Expired', bg: alpha('#ef4444', 0.1), color: '#dc2626' },
  revoked: { label: 'Revoked', bg: alpha('#64748b', 0.1), color: '#475569' },
  on_hold: { label: 'On Hold', bg: alpha('#f59e0b', 0.1), color: '#d97706' }
};

const priorityConfig = {
  low: { color: '#64748b', bg: alpha('#64748b', 0.1) },
  medium: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1) },
  high: { color: '#ef4444', bg: alpha('#ef4444', 0.1) },
  urgent: { color: '#dc2626', bg: alpha('#dc2626', 0.15) }
};

const commonPayers = [
  'Aetna', 'Anthem Blue Cross', 'Blue Cross Blue Shield', 'Cigna', 'Humana',
  'UnitedHealthcare', 'Medicare', 'Medicaid', 'Tricare', 'Kaiser Permanente',
  'Molina Healthcare', 'Centene', 'WellCare', 'Amerigroup', 'Oscar Health',
  'Ambetter', 'Magellan Health', 'Beacon Health', 'Optum', 'MultiPlan'
];

const docChecklistTemplates = {
  'Initial Credentialing': [
    { documentName: 'State Medical License', required: true, category: 'provider_doc' },
    { documentName: 'DEA Certificate', required: true, category: 'provider_doc' },
    { documentName: 'Board Certification', required: true, category: 'provider_doc' },
    { documentName: 'NPI Confirmation', required: true, category: 'provider_doc' },
    { documentName: 'Malpractice Insurance (COI)', required: true, category: 'provider_doc' },
    { documentName: 'CV / Resume', required: true, category: 'provider_doc' },
    { documentName: 'Medical School Diploma', required: true, category: 'provider_doc' },
    { documentName: 'W-9 Form', required: true, category: 'group_doc' },
    { documentName: 'Group Practice Agreement', required: false, category: 'group_doc' },
    { documentName: 'CLIA Certificate', required: false, category: 'group_doc' },
    { documentName: 'Completed Application Form', required: true, category: 'application_form' },
    { documentName: 'Credentialing Attestation', required: true, category: 'application_form' },
    { documentName: 'CAQH Application', required: false, category: 'supporting' },
  ],
  'Payer Enrollment': [
    { documentName: 'Provider Enrollment Application', required: true, category: 'application_form' },
    { documentName: 'W-9 Form', required: true, category: 'group_doc' },
    { documentName: 'State License Copy', required: true, category: 'provider_doc' },
    { documentName: 'DEA Certificate Copy', required: true, category: 'provider_doc' },
    { documentName: 'NPI Verification', required: true, category: 'provider_doc' },
    { documentName: 'Malpractice COI', required: true, category: 'provider_doc' },
    { documentName: 'Voided Check / Direct Deposit Form', required: true, category: 'group_doc' },
    { documentName: 'Group NPI Confirmation', required: false, category: 'group_doc' },
    { documentName: 'Tax ID Verification', required: true, category: 'group_doc' },
  ],
  'Renewal / Revalidation': [
    { documentName: 'Updated State License', required: true, category: 'provider_doc' },
    { documentName: 'Updated DEA Certificate', required: true, category: 'provider_doc' },
    { documentName: 'Updated Malpractice COI', required: true, category: 'provider_doc' },
    { documentName: 'CAQH Attestation', required: true, category: 'supporting' },
    { documentName: 'Revalidation Form', required: true, category: 'application_form' },
  ]
};

const emptyForm = {
  provider: '', groupPractice: '', applicationType: 'initial', applicationName: '',
  applicationStatus: 'not_started', priority: 'medium',
  insurancePayer: { name: '', payerId: '', contactName: '', contactPhone: '', contactEmail: '', contactFax: '', portalUrl: '', portalUsername: '', portalNotes: '', submissionMethod: 'online_portal' },
  applicationTracker: { submittedDate: '', receivedDate: '', assignedAnalyst: '', analystPhone: '', analystEmail: '', effectiveDate: '', approvalDate: '', denialReason: '', providerId: '', groupId: '', contractType: '', feeSchedule: '', estimatedDays: '' },
  documentChecklist: [],
  licenseType: '', licenseNumber: '', issuedDate: '', expiryDate: '', notes: ''
};

const Credentials = () => {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [selectedProviderObj, setSelectedProviderObj] = useState(null);
  const [selectedGroupPracticeObj, setSelectedGroupPracticeObj] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [formStep, setFormStep] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCredId, setUploadCredId] = useState(null);
  const [uploadChecklistIdx, setUploadChecklistIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [provDocOpen, setProvDocOpen] = useState(false);
  const [provDocProvider, setProvDocProvider] = useState(null);
  const [provDocs, setProvDocs] = useState([]);
  const [provDocUploading, setProvDocUploading] = useState(false);
  const provFileRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanProvider, setScanProvider] = useState(null);
  const [scanMode, setScanMode] = useState('file');
  const [scanText, setScanText] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scanFileRef = useRef(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpCredId, setFollowUpCredId] = useState(null);
  const [followUpNotes, setFollowUpNotes] = useState('');

  const uniqById = useCallback((list) => {
    const seen = new Set();
    const out = [];
    for (const item of (Array.isArray(list) ? list : [])) {
      const id = item?._id ?? item?.id;
      if (!id) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }, []);

  const sortProvidersByName = useCallback((list) => {
    return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
      const an = (fullName(a) || '').toLowerCase();
      const bn = (fullName(b) || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, []);

  const loadProviders = useCallback(async (includeInactive) => {
    setProvidersLoading(true);
    try {
      const params = { limit: 10000, includeDetails: false };
      if (!includeInactive) params.isActive = true;
      const p = await providersAPI.getAll(params);
      const list = uniqById(Array.isArray(p.data) ? p.data : p.data?.data || []);
      setProviders(sortProvidersByName(list));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  }, [sortProvidersByName, uniqById]);

  const providerOptions = useMemo(() => {
    const base = includeInactiveProviders ? providers : providers.filter(p => p.isActive !== false);
    if (includeInactiveProviders) return base;
    const injected = [];
    if (selectedProviderObj && selectedProviderObj.isActive === false) injected.push(selectedProviderObj);
    if (selectedGroupPracticeObj && selectedGroupPracticeObj.isActive === false) injected.push(selectedGroupPracticeObj);
    if (scanProvider && scanProvider.isActive === false) injected.push(scanProvider);
    const merged = uniqById([...injected, ...base]);
    return sortProvidersByName(merged);
  }, [providers, includeInactiveProviders, selectedProviderObj, selectedGroupPracticeObj, scanProvider, sortProvidersByName, uniqById]);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        credentialsAPI.getAll({ limit: 1000 }),
        credentialsAPI.getStats().catch(() => ({ data: {} }))
      ]);
      setRows(Array.isArray(c.data) ? c.data : c.data?.data || []);
      setStats(s.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Credentialing | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProviders(includeInactiveProviders); }, [includeInactiveProviders, loadProviders]);

  useEffect(() => {
    if (!form.provider) { setSelectedProviderObj(null); return; }
    const found = providers.find(p => String(p._id ?? p.id) === String(form.provider));
    if (found) setSelectedProviderObj(found);
  }, [form.provider, providers]);

  useEffect(() => {
    if (!form.groupPractice) { setSelectedGroupPracticeObj(null); return; }
    const found = providers.find(p => String(p._id ?? p.id) === String(form.groupPractice));
    if (found) setSelectedGroupPracticeObj(found);
  }, [form.groupPractice, providers]);

  const searchFiltered = rows.filter(r => {
    if (statusFilter !== 'all' && r.applicationStatus !== statusFilter) return false;
    const pn = fullName(r.provider) || '';
    const payer = r.insurancePayer?.name || '';
    return [pn, payer, r.applicationName || '', r.licenseNumber || '', r.credentialId || '']
      .some(v => v.toLowerCase().includes(search.toLowerCase()));
  });

  const credColumns = useMemo(() => [
    { id: 'provider', label: 'Provider', getValue: r => fullName(r.provider) || '' },
    { id: 'payer', label: 'Payer / Application', getValue: r => r.insurancePayer?.name || r.applicationName || '' },
    { id: 'applicationType', label: 'Type', getValue: r => r.applicationType || '' },
    { id: 'applicationStatus', label: 'Status', getValue: r => r.applicationStatus || '' },
    { id: 'priority', label: 'Priority', getValue: r => r.priority || 'medium' },
    { id: 'docs', label: 'Docs', getValue: r => (r.documentChecklist?.filter(d => d.received)?.length || 0), getDisplay: r => `${r.documentChecklist?.filter(d => d.received)?.length || 0}/${r.documentChecklist?.length || 0}` },
    { id: 'expiry', label: 'Expiry', getValue: r => r.expiryDate || '', getDisplay: r => r.expiryDate ? new Date(r.expiryDate).toLocaleDateString() : '—' },
  ], []);

  const { sortedData: filtered, renderHeader: renderCredHeader, renderFilterPopover: renderCredFilterPopover, renderClearFilters: renderCredClearFilters } = useTableSort(credColumns, searchFiltered);

  const F = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const FPayer = (f, v) => setForm(p => ({ ...p, insurancePayer: { ...p.insurancePayer, [f]: v } }));
  const FTracker = (f, v) => setForm(p => ({ ...p, applicationTracker: { ...p.applicationTracker, [f]: v } }));

  const handleOpen = (row) => {
    if (row) {
      setEditing(row._id);
      setForm({
        provider: row.provider?._id || '', groupPractice: row.groupPractice?._id || '',
        applicationType: row.applicationType || 'initial', applicationName: row.applicationName || '',
        applicationStatus: row.applicationStatus || 'not_started', priority: row.priority || 'medium',
        insurancePayer: { name: row.insurancePayer?.name || '', payerId: row.insurancePayer?.payerId || '', contactName: row.insurancePayer?.contactName || '', contactPhone: row.insurancePayer?.contactPhone || '', contactEmail: row.insurancePayer?.contactEmail || '', contactFax: row.insurancePayer?.contactFax || '', portalUrl: row.insurancePayer?.portalUrl || '', portalUsername: row.insurancePayer?.portalUsername || '', portalNotes: row.insurancePayer?.portalNotes || '', submissionMethod: row.insurancePayer?.submissionMethod || 'online_portal' },
        applicationTracker: { submittedDate: row.applicationTracker?.submittedDate?.slice?.(0, 10) || '', receivedDate: row.applicationTracker?.receivedDate?.slice?.(0, 10) || '', assignedAnalyst: row.applicationTracker?.assignedAnalyst || '', analystPhone: row.applicationTracker?.analystPhone || '', analystEmail: row.applicationTracker?.analystEmail || '', effectiveDate: row.applicationTracker?.effectiveDate?.slice?.(0, 10) || '', approvalDate: row.applicationTracker?.approvalDate?.slice?.(0, 10) || '', denialReason: row.applicationTracker?.denialReason || '', providerId: row.applicationTracker?.providerId || '', groupId: row.applicationTracker?.groupId || '', contractType: row.applicationTracker?.contractType || '', feeSchedule: row.applicationTracker?.feeSchedule || '', estimatedDays: row.applicationTracker?.estimatedDays || '' },
        documentChecklist: row.documentChecklist || [],
        licenseType: row.licenseType || '', licenseNumber: row.licenseNumber || '',
        issuedDate: row.issuedDate?.slice?.(0, 10) || '', expiryDate: row.expiryDate?.slice?.(0, 10) || '', notes: row.notes || ''
      });
    } else {
      setEditing(null);
      setForm({ ...emptyForm, insurancePayer: { ...emptyForm.insurancePayer }, applicationTracker: { ...emptyForm.applicationTracker }, documentChecklist: [] });
    }
    setFormStep(0);
    setFormOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) await credentialsAPI.update(editing, form);
      else await credentialsAPI.create(form);
      setFormOpen(false); load();
      toast.success(editing ? 'Application updated' : 'Application created');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async () => {
    try { await credentialsAPI.delete(deleteId); setDeleteId(null); load(); toast.success('Deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await credentialsAPI.getById(id);
      setDetailData(res.data);
      setDetailTab(0);
      setDetailOpen(true);
    } catch (e) { toast.error('Failed to load details'); }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      fd.append('type', 'credential');
      if (uploadChecklistIdx !== null) fd.append('checklistIndex', uploadChecklistIdx);
      await credentialsAPI.uploadDocument(uploadCredId, fd);
      toast.success('Document uploaded');
      if (detailData) {
        const res = await credentialsAPI.getById(detailData._id);
        setDetailData(res.data);
      }
      setUploadOpen(false); load();
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleRemoveDoc = async (credId, docId) => {
    try {
      await credentialsAPI.removeDocument(credId, docId);
      toast.success('Document removed');
      if (detailData) {
        const res = await credentialsAPI.getById(detailData._id);
        setDetailData(res.data);
      }
    } catch (e) { toast.error('Remove failed'); }
  };

  const handleProvDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !provDocProvider) return;
    setProvDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      fd.append('type', 'credential');
      await credentialsAPI.uploadProviderDocument(provDocProvider._id, fd);
      toast.success('Document uploaded');
      const docs = await credentialsAPI.getProviderDocuments(provDocProvider._id);
      setProvDocs(Array.isArray(docs.data) ? docs.data : []);
    } catch (e) { toast.error('Upload failed'); }
    finally { setProvDocUploading(false); if (provFileRef.current) provFileRef.current.value = ''; }
  };

  const openProvDocs = async (provider) => {
    setProvDocProvider(provider);
    setProvDocOpen(true);
    try {
      const docs = await credentialsAPI.getProviderDocuments(provider._id);
      setProvDocs(Array.isArray(docs.data) ? docs.data : []);
    } catch (e) { setProvDocs([]); }
  };

  const handleScan = async () => {
    if (!scanProvider) return;
    setScanning(true);
    try {
      let res;
      if (scanMode === 'paste') {
        res = await credentialsAPI.caqhManual(scanProvider._id, { text: scanText });
      } else {
        const file = scanFileRef.current?.files?.[0];
        if (!file) { toast.error('Select a file'); setScanning(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        res = await credentialsAPI.caqhScan(scanProvider._id, fd);
      }
      setScanResult(res.data?.extractedData || {});
      toast.success('Scan complete - review extracted data');
    } catch (e) { toast.error('Scan failed'); }
    finally { setScanning(false); }
  };

  const handleApplyScan = async () => {
    if (!scanProvider || !scanResult) return;
    try {
      await credentialsAPI.caqhApply(scanProvider._id, { caqhData: scanResult });
      toast.success('CAQH data applied to provider profile');
      setScanOpen(false); setScanResult(null); setScanText(''); load();
    } catch (e) { toast.error('Failed to apply data'); }
  };

  const handleFollowUp = async () => {
    try {
      await credentialsAPI.addFollowUp(followUpCredId, { notes: followUpNotes });
      toast.success('Follow-up recorded');
      setFollowUpOpen(false); setFollowUpNotes('');
      if (detailData?._id === followUpCredId) {
        const res = await credentialsAPI.getById(followUpCredId);
        setDetailData(res.data);
      }
    } catch (e) { toast.error('Failed'); }
  };

  const applyTemplate = (templateName) => {
    const template = docChecklistTemplates[templateName];
    if (template) {
      setForm(p => ({ ...p, documentChecklist: template.map(t => ({ ...t, received: false, receivedDate: null, document: null, notes: '' })) }));
    }
  };

  const daysUntilExpiry = (date) => {
    if (!date) return null;
    return Math.ceil((new Date(date) - new Date()) / 86400000);
  };

  const groupPractices = providerOptions.filter(p => p.isGroupPractice);
  const formSteps = ['Application Info', 'Insurance Payer', 'Documents', 'Tracking'];
  const lbl = (text) => (
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>{text}</Typography>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Insurance Credentialing</Typography>
          <Typography variant="body2" color="text.secondary">Manage provider credentialing, payer enrollments, and document tracking</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<Scanner />} onClick={() => { setScanProvider(null); setScanOpen(true); setScanResult(null); setScanText(''); }}
            sx={{ borderColor: '#8b5cf6', color: '#7c3aed', fontWeight: 700, '&:hover': { borderColor: '#7c3aed', bgcolor: alpha('#8b5cf6', 0.06) } }}>
            CAQH Scanner
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
            sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 4px 14px rgba(245,158,11,0.3)', px: 3, fontWeight: 700 }}>
            New Application
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Applications', value: stats.total || 0, color: '#3b82f6', sub: `${stats.active || 0} active` },
          { label: 'Pending / In Progress', value: (stats.pending || 0) + (stats.submitted || 0) + (stats.inReview || 0), color: '#f59e0b', sub: `${stats.submitted || 0} submitted` },
          { label: 'Follow-Up Required', value: stats.followUp || 0, color: '#ec4899', sub: 'Needs attention' },
          { label: 'Expiring (30 days)', value: stats.expiring30 || 0, color: '#ef4444', sub: `${stats.expired || 0} expired` },
          { label: 'Approved', value: stats.approved || 0, color: '#10b981', sub: 'Completed' },
          { label: 'Denied', value: stats.denied || 0, color: '#64748b', sub: 'Resubmit needed' },
        ].map((s, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Card sx={{ borderTop: `3px solid ${s.color}`, height: '100%' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, mt: 0.5 }}>{s.value}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{s.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search applications..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 320 }} />
            <Tabs value={statusFilter} onChange={(_, v) => { setStatusFilter(v); setPage(0); }}
              sx={{ '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none' } }}>
              <Tab value="all" label="All" />
              <Tab value="not_started" label="Not Started" />
              <Tab value="submitted" label="Submitted" />
              <Tab value="in_review" label="In Review" />
              <Tab value="follow_up" label={<Badge badgeContent={stats.followUp || 0} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}>Follow Up</Badge>} />
              <Tab value="approved" label="Approved" />
              <Tab value="active" label="Active" />
            </Tabs>
            {renderCredClearFilters()}
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  {renderCredHeader(<TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Actions</TableCell>)}
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                          <VerifiedUser sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No applications found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => {
                        const sc = statusConfig[r.applicationStatus] || statusConfig.pending;
                        const pc = priorityConfig[r.priority] || priorityConfig.medium;
                        const days = daysUntilExpiry(r.expiryDate);
                        const checklistTotal = r.documentChecklist?.length || 0;
                        const checklistDone = r.documentChecklist?.filter(d => d.received)?.length || 0;
                        return (
                          <TableRow key={r._id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#f59e0b', 0.03) } }}
                            onClick={() => handleViewDetail(r._id)}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 34, height: 34, fontSize: '0.78rem', fontWeight: 700, bgcolor: alpha('#f59e0b', 0.1), color: '#d97706' }}>
                                  {(r.provider?.firstName || '?')[0]}
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.2 }}>{fullName(r.provider) || 'N/A'}</Typography>
                                  {r.provider?.groupName && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{r.provider.groupName}</Typography>}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{r.insurancePayer?.name || r.applicationName || '—'}</Typography>
                              {r.insurancePayer?.name && r.applicationName && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{r.applicationName}</Typography>}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={(r.applicationType || 'initial').replace('_', ' ')} sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={sc.label} sx={{ fontWeight: 700, height: 24, bgcolor: sc.bg, color: sc.color, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={r.priority || 'medium'} sx={{ height: 22, fontWeight: 700, bgcolor: pc.bg, color: pc.color, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell>
                              {checklistTotal > 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LinearProgress variant="determinate" value={(checklistDone / checklistTotal) * 100}
                                    sx={{ width: 50, height: 6, borderRadius: 3, bgcolor: alpha('#10b981', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 3 } }} />
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>{checklistDone}/{checklistTotal}</Typography>
                                </Box>
                              ) : <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>—</Typography>}
                            </TableCell>
                            <TableCell>
                              {r.expiryDate ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.78rem' }}>{new Date(r.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</Typography>
                                  {days !== null && days <= 60 && (
                                    <Chip size="small" label={days <= 0 ? 'EXP' : `${days}d`}
                                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha(days <= 0 ? '#ef4444' : days <= 30 ? '#ef4444' : '#f59e0b', 0.1), color: days <= 30 ? '#ef4444' : '#d97706' }} />
                                  )}
                                </Box>
                              ) : '—'}
                            </TableCell>
                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                              <Tooltip title="View"><IconButton size="small" onClick={() => handleViewDetail(r._id)} sx={{ color: '#3b82f6' }}><Visibility sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Upload Doc"><IconButton size="small" onClick={() => { setUploadCredId(r._id); setUploadChecklistIdx(null); setUploadOpen(true); }} sx={{ color: '#8b5cf6' }}><CloudUpload sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Provider Docs"><IconButton size="small" onClick={() => r.provider && openProvDocs(r.provider)} sx={{ color: '#10b981' }}><FolderOpen sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(r)} sx={{ color: '#f59e0b' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>
      {renderCredFilterPopover()}

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: 2 }}>
              <VerifiedUser sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography sx={{ fontWeight: 700 }}>{editing ? 'Edit Application' : 'New Credentialing Application'}</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <Box sx={{ px: 3, pt: 2 }}>
          <Stepper activeStep={formStep} alternativeLabel sx={{ mb: 2 }}>
            {formSteps.map(label => <Step key={label}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.78rem' } }}>{label}</StepLabel></Step>)}
          </Stepper>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {formStep === 0 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                  {lbl('Provider *')}
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Checkbox size="small" checked={includeInactiveProviders} onChange={(e) => setIncludeInactiveProviders(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Include inactive</Typography>}
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  select
                  disabled={providersLoading}
                  value={form.provider}
                  onChange={e => {
                    const id = e.target.value;
                    F('provider', id);
                    setSelectedProviderObj(providerOptions.find(p => String(p._id ?? p.id) === String(id)) || null);
                  }}>
                  <MenuItem value="">{providersLoading ? 'Loading providers…' : 'Select provider'}</MenuItem>
                  {providerOptions.map(p => (
                    <MenuItem key={p._id} value={p._id}>
                      {fullName(p)}{p.isGroupPractice ? ' (Group)' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>{lbl('Group Practice')}<TextField fullWidth size="small" select value={form.groupPractice} onChange={e => F('groupPractice', e.target.value)}>
                <MenuItem value="">None</MenuItem>
                {groupPractices.map(p => <MenuItem key={p._id} value={p._id}>{p.groupName || fullName(p)}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={6}>{lbl('Application Type *')}<TextField fullWidth size="small" select value={form.applicationType} onChange={e => F('applicationType', e.target.value)}>
                {[{ v: 'initial', l: 'Initial Credentialing' }, { v: 'renewal', l: 'Renewal' }, { v: 'revalidation', l: 'Revalidation' }, { v: 'payer_enrollment', l: 'Payer Enrollment' }, { v: 'add_location', l: 'Add Location' }, { v: 'other', l: 'Other' }].map(t => <MenuItem key={t.v} value={t.v}>{t.l}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={6}>{lbl('Application Name *')}<TextField fullWidth size="small" value={form.applicationName} onChange={e => F('applicationName', e.target.value)} placeholder="e.g. Aetna Initial Credentialing" /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Status')}<TextField fullWidth size="small" select value={form.applicationStatus} onChange={e => F('applicationStatus', e.target.value)}>
                {Object.entries(statusConfig).map(([v, c]) => <MenuItem key={v} value={v}>{c.label}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={4}>{lbl('Priority')}<TextField fullWidth size="small" select value={form.priority} onChange={e => F('priority', e.target.value)}>
                {['low', 'medium', 'high', 'urgent'].map(p => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={4}>{lbl('License Type')}<TextField fullWidth size="small" select value={form.licenseType} onChange={e => F('licenseType', e.target.value)}>
                <MenuItem value="">None</MenuItem>
                {['Medical License', 'DEA', 'NPI', 'Board Certification', 'Malpractice Insurance', 'CLIA', 'State License', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={4}>{lbl('License Number')}<TextField fullWidth size="small" value={form.licenseNumber} onChange={e => F('licenseNumber', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Issued Date')}<TextField fullWidth size="small" type="date" value={form.issuedDate} onChange={e => F('issuedDate', e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Expiry Date')}<TextField fullWidth size="small" type="date" value={form.expiryDate} onChange={e => F('expiryDate', e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12}>{lbl('Notes')}<TextField fullWidth size="small" multiline rows={2} value={form.notes} onChange={e => F('notes', e.target.value)} /></Grid>
            </Grid>
          )}
          {formStep === 1 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>{lbl('Insurance Payer *')}<TextField fullWidth size="small" select value={form.insurancePayer.name} onChange={e => FPayer('name', e.target.value)}>
                <MenuItem value="">Select payer</MenuItem>
                {commonPayers.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={6}>{lbl('Payer ID')}<TextField fullWidth size="small" value={form.insurancePayer.payerId} onChange={e => FPayer('payerId', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Contact Name')}<TextField fullWidth size="small" value={form.insurancePayer.contactName} onChange={e => FPayer('contactName', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Contact Phone')}<TextField fullWidth size="small" value={form.insurancePayer.contactPhone} onChange={e => FPayer('contactPhone', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Contact Email')}<TextField fullWidth size="small" value={form.insurancePayer.contactEmail} onChange={e => FPayer('contactEmail', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Contact Fax')}<TextField fullWidth size="small" value={form.insurancePayer.contactFax} onChange={e => FPayer('contactFax', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Portal URL')}<TextField fullWidth size="small" value={form.insurancePayer.portalUrl} onChange={e => FPayer('portalUrl', e.target.value)} placeholder="https://" /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Portal Username')}<TextField fullWidth size="small" value={form.insurancePayer.portalUsername} onChange={e => FPayer('portalUsername', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Submission Method')}<TextField fullWidth size="small" select value={form.insurancePayer.submissionMethod} onChange={e => FPayer('submissionMethod', e.target.value)}>
                {[{ v: 'online_portal', l: 'Online Portal' }, { v: 'email', l: 'Email' }, { v: 'fax', l: 'Fax' }, { v: 'mail', l: 'Mail' }, { v: 'caqh', l: 'CAQH' }, { v: 'other', l: 'Other' }].map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={12} sm={6}>{lbl('Portal Notes')}<TextField fullWidth size="small" value={form.insurancePayer.portalNotes} onChange={e => FPayer('portalNotes', e.target.value)} /></Grid>
            </Grid>
          )}
          {formStep === 2 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Document Checklist</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.keys(docChecklistTemplates).map(t => (
                    <Button key={t} size="small" variant="outlined" onClick={() => applyTemplate(t)}
                      sx={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#e2e8f0' }}>{t}</Button>
                  ))}
                </Box>
              </Box>
              {form.documentChecklist.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>Choose a template above or add documents manually.</Alert>}
              <List dense>
                {form.documentChecklist.map((item, idx) => (
                  <ListItem key={idx} sx={{ bgcolor: idx % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 2, mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox size="small" checked={item.received || false} onChange={e => {
                        const updated = [...form.documentChecklist];
                        updated[idx] = { ...updated[idx], received: e.target.checked, receivedDate: e.target.checked ? new Date().toISOString() : null };
                        setForm(p => ({ ...p, documentChecklist: updated }));
                      }} sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600, textDecoration: item.received ? 'line-through' : 'none', color: item.received ? '#94a3b8' : 'text.primary' }}>{item.documentName}</Typography>}
                      secondary={<Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
                        <Chip size="small" label={item.required ? 'Required' : 'Optional'} sx={{ height: 18, fontSize: '0.65rem', bgcolor: item.required ? alpha('#ef4444', 0.1) : alpha('#64748b', 0.1), color: item.required ? '#ef4444' : '#64748b' }} />
                        <Chip size="small" label={(item.category || 'other').replace('_', ' ')} sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                      </Box>}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => setForm(p => ({ ...p, documentChecklist: p.documentChecklist.filter((_, i) => i !== idx) }))}><Delete sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Button size="small" startIcon={<Add />}
                onClick={() => setForm(p => ({ ...p, documentChecklist: [...p.documentChecklist, { documentName: 'New Document', required: false, received: false, category: 'other', notes: '' }] }))}
                sx={{ mt: 1, color: '#3b82f6', fontWeight: 600 }}>Add Item</Button>
            </Box>
          )}
          {formStep === 3 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>{lbl('Submitted Date')}<TextField fullWidth size="small" type="date" value={form.applicationTracker.submittedDate} onChange={e => FTracker('submittedDate', e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Received/Acknowledged Date')}<TextField fullWidth size="small" type="date" value={form.applicationTracker.receivedDate} onChange={e => FTracker('receivedDate', e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Assigned Analyst')}<TextField fullWidth size="small" value={form.applicationTracker.assignedAnalyst} onChange={e => FTracker('assignedAnalyst', e.target.value)} /></Grid>
              <Grid item xs={12} sm={3}>{lbl('Analyst Phone')}<TextField fullWidth size="small" value={form.applicationTracker.analystPhone} onChange={e => FTracker('analystPhone', e.target.value)} /></Grid>
              <Grid item xs={12} sm={3}>{lbl('Analyst Email')}<TextField fullWidth size="small" value={form.applicationTracker.analystEmail} onChange={e => FTracker('analystEmail', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Effective Date')}<TextField fullWidth size="small" type="date" value={form.applicationTracker.effectiveDate} onChange={e => FTracker('effectiveDate', e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Approval Date')}<TextField fullWidth size="small" type="date" value={form.applicationTracker.approvalDate} onChange={e => FTracker('approvalDate', e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Estimated Days')}<TextField fullWidth size="small" type="number" value={form.applicationTracker.estimatedDays} onChange={e => FTracker('estimatedDays', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Payer Provider ID')}<TextField fullWidth size="small" value={form.applicationTracker.providerId} onChange={e => FTracker('providerId', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Payer Group ID')}<TextField fullWidth size="small" value={form.applicationTracker.groupId} onChange={e => FTracker('groupId', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}>{lbl('Contract Type')}<TextField fullWidth size="small" value={form.applicationTracker.contractType} onChange={e => FTracker('contractType', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Fee Schedule')}<TextField fullWidth size="small" value={form.applicationTracker.feeSchedule} onChange={e => FTracker('feeSchedule', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>{lbl('Denial Reason')}<TextField fullWidth size="small" value={form.applicationTracker.denialReason} onChange={e => FTracker('denialReason', e.target.value)} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => formStep > 0 ? setFormStep(formStep - 1) : setFormOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>
            {formStep > 0 ? 'Back' : 'Cancel'}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {formStep < 3 && <Button variant="outlined" onClick={() => setFormStep(formStep + 1)} sx={{ fontWeight: 600 }}>Next</Button>}
            <Button variant="contained" onClick={handleSave} sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', fontWeight: 700, px: 4 }}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* DETAIL VIEW DIALOG */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4, minHeight: '70vh' } }}>
        {detailData && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 44, height: 44, background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: 2 }}>
                    <VerifiedUser sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{detailData.applicationName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{fullName(detailData.provider)} | {detailData.credentialId}</Typography>
                      <Chip size="small" label={statusConfig[detailData.applicationStatus]?.label || detailData.applicationStatus}
                        sx={{ height: 22, fontWeight: 700, bgcolor: statusConfig[detailData.applicationStatus]?.bg, color: statusConfig[detailData.applicationStatus]?.color }} />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<Phone />} onClick={() => { setFollowUpCredId(detailData._id); setFollowUpOpen(true); }}
                    sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Follow Up</Button>
                  <Button size="small" variant="outlined" startIcon={<CloudUpload />} onClick={() => { setUploadCredId(detailData._id); setUploadChecklistIdx(null); setUploadOpen(true); }}
                    sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Upload</Button>
                  <IconButton onClick={() => setDetailOpen(false)} size="small"><Close /></IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <Divider />
            <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ px: 3, '& .MuiTab-root': { fontSize: '0.82rem', fontWeight: 600, textTransform: 'none' } }}>
              <Tab label="Overview" />
              <Tab label={`Documents (${(detailData.documents?.length || 0) + (detailData.documentChecklist?.length || 0)})`} />
              <Tab label="Payer Info" />
              <Tab label="Tracking" />
              <Tab label={`Activity (${detailData.activityLog?.length || 0})`} />
            </Tabs>
            <Divider />
            <DialogContent sx={{ pt: 3, minHeight: 400 }}>
              {detailTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person sx={{ fontSize: 18, color: '#3b82f6' }} /> Provider Information
                      </Typography>
                      {[
                        { l: 'Provider', v: fullName(detailData.provider) },
                        { l: 'NPI', v: detailData.provider?.npiNumber || '—' },
                        { l: 'Group', v: detailData.groupPractice ? fullName(detailData.groupPractice) || detailData.groupPractice?.groupName : '—' },
                        { l: 'Application Type', v: (detailData.applicationType || 'initial').replace('_', ' ') },
                        { l: 'License Type', v: detailData.licenseType || '—' },
                        { l: 'License #', v: detailData.licenseNumber || '—' },
                        { l: 'Issued', v: detailData.issuedDate ? new Date(detailData.issuedDate).toLocaleDateString() : '—' },
                        { l: 'Expires', v: detailData.expiryDate ? new Date(detailData.expiryDate).toLocaleDateString() : '—' },
                      ].map((r, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{r.l}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, textTransform: 'capitalize' }}>{r.v}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assignment sx={{ fontSize: 18, color: '#f59e0b' }} /> Application Status
                      </Typography>
                      {[
                        { l: 'Status', v: statusConfig[detailData.applicationStatus]?.label || detailData.applicationStatus },
                        { l: 'Priority', v: detailData.priority || 'Medium' },
                        { l: 'Submitted', v: detailData.applicationTracker?.submittedDate ? new Date(detailData.applicationTracker.submittedDate).toLocaleDateString() : 'Not yet' },
                        { l: 'Effective Date', v: detailData.applicationTracker?.effectiveDate ? new Date(detailData.applicationTracker.effectiveDate).toLocaleDateString() : '—' },
                        { l: 'Analyst', v: detailData.applicationTracker?.assignedAnalyst || '—' },
                        { l: 'Payer Provider ID', v: detailData.applicationTracker?.providerId || '—' },
                        { l: 'Payer Group ID', v: detailData.applicationTracker?.groupId || '—' },
                        { l: 'Created', v: detailData.createdAt ? new Date(detailData.createdAt).toLocaleDateString() : '—' },
                      ].map((r, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{r.l}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, textTransform: 'capitalize' }}>{r.v}</Typography>
                        </Box>
                      ))}
                    </Paper>
                    {detailData.notes && (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>Notes</Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{detailData.notes}</Typography>
                      </Paper>
                    )}
                  </Grid>
                </Grid>
              )}
              {detailTab === 1 && (
                <Box>
                  {detailData.documentChecklist?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assignment sx={{ fontSize: 18, color: '#f59e0b' }} /> Document Checklist
                        <Chip size="small" label={`${detailData.documentChecklist.filter(d => d.received).length}/${detailData.documentChecklist.length}`}
                          sx={{ fontWeight: 700, bgcolor: alpha('#10b981', 0.1), color: '#059669' }} />
                      </Typography>
                      <List dense>
                        {detailData.documentChecklist.map((item, idx) => (
                          <ListItem key={idx} sx={{ bgcolor: idx % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 2, mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {item.received ? <CheckCircle sx={{ color: '#10b981', fontSize: 22 }} /> : <InsertDriveFile sx={{ color: '#94a3b8', fontSize: 22 }} />}
                            </ListItemIcon>
                            <ListItemText
                              primary={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>{item.documentName}</Typography>}
                              secondary={<Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
                                <Chip size="small" label={item.required ? 'Required' : 'Optional'} sx={{ height: 18, fontSize: '0.65rem', bgcolor: item.required ? alpha('#ef4444', 0.1) : alpha('#64748b', 0.1), color: item.required ? '#ef4444' : '#64748b' }} />
                                <Chip size="small" label={(item.category || '').replace('_', ' ')} sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                                {item.received && <Chip size="small" label={`Received ${item.receivedDate ? new Date(item.receivedDate).toLocaleDateString() : ''}`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha('#10b981', 0.1), color: '#059669' }} />}
                              </Box>}
                            />
                            <ListItemSecondaryAction>
                              {item.received && item.document ? (
                                <Tooltip title="Download"><IconButton size="small" onClick={() => {
                                  const docId = typeof item.document === 'object' ? item.document._id : item.document;
                                  window.open(`${API_BASE}/documents/${docId}/download`, '_blank');
                                }}><Download sx={{ fontSize: 18, color: '#3b82f6' }} /></IconButton></Tooltip>
                              ) : (
                                <Button size="small" startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                                  onClick={() => { setUploadCredId(detailData._id); setUploadChecklistIdx(idx); setUploadOpen(true); }}
                                  sx={{ fontSize: '0.72rem', fontWeight: 600 }}>Upload</Button>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFile sx={{ fontSize: 18, color: '#3b82f6' }} /> Uploaded Documents ({detailData.documents?.length || 0})
                  </Typography>
                  {(!detailData.documents || detailData.documents.length === 0) ? (
                    <Alert severity="info">No documents uploaded yet.</Alert>
                  ) : (
                    <List dense>
                      {detailData.documents.map((doc, idx) => (
                        <ListItem key={idx} sx={{ bgcolor: '#f8fafc', borderRadius: 2, mb: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}><Description sx={{ color: '#3b82f6', fontSize: 22 }} /></ListItemIcon>
                          <ListItemText
                            primary={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>{doc.name || doc.originalname}</Typography>}
                            secondary={<Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                              {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(0)} KB` : ''} | {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ''}
                            </Typography>}
                          />
                          <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => window.open(`${API_BASE}/documents/${doc._id}/download`, '_blank')} sx={{ color: '#3b82f6' }}><Download sx={{ fontSize: 18 }} /></IconButton>
                            <IconButton size="small" onClick={() => handleRemoveDoc(detailData._id, doc._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
              {detailTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business sx={{ fontSize: 18, color: '#3b82f6' }} /> Insurance Payer
                      </Typography>
                      {[
                        { l: 'Payer Name', v: detailData.insurancePayer?.name || '—' },
                        { l: 'Payer ID', v: detailData.insurancePayer?.payerId || '—' },
                        { l: 'Contact', v: detailData.insurancePayer?.contactName || '—' },
                        { l: 'Phone', v: detailData.insurancePayer?.contactPhone || '—' },
                        { l: 'Email', v: detailData.insurancePayer?.contactEmail || '—' },
                        { l: 'Fax', v: detailData.insurancePayer?.contactFax || '—' },
                        { l: 'Portal', v: detailData.insurancePayer?.portalUrl || '—' },
                        { l: 'Submission', v: (detailData.insurancePayer?.submissionMethod || '').replace('_', ' ') || '—' },
                      ].map((r, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{r.l}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.v}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2 }}>Payer Enrollments</Typography>
                      {(!detailData.payerEnrollments || detailData.payerEnrollments.length === 0) ? (
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>No enrollments</Typography>
                      ) : detailData.payerEnrollments.map((pe, i) => (
                        <Box key={i} sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, mb: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{pe.payerName}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>ID: {pe.enrollmentId || '—'} | Status: {pe.status}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>
              )}
              {detailTab === 3 && (
                <Box>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Timeline sx={{ fontSize: 18, color: '#f59e0b' }} /> Application Tracking
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { l: 'Submitted Date', v: detailData.applicationTracker?.submittedDate ? new Date(detailData.applicationTracker.submittedDate).toLocaleDateString() : '—' },
                        { l: 'Received Date', v: detailData.applicationTracker?.receivedDate ? new Date(detailData.applicationTracker.receivedDate).toLocaleDateString() : '—' },
                        { l: 'Assigned Analyst', v: detailData.applicationTracker?.assignedAnalyst || '—' },
                        { l: 'Analyst Phone', v: detailData.applicationTracker?.analystPhone || '—' },
                        { l: 'Effective Date', v: detailData.applicationTracker?.effectiveDate ? new Date(detailData.applicationTracker.effectiveDate).toLocaleDateString() : '—' },
                        { l: 'Approval Date', v: detailData.applicationTracker?.approvalDate ? new Date(detailData.applicationTracker.approvalDate).toLocaleDateString() : '—' },
                        { l: 'Provider ID', v: detailData.applicationTracker?.providerId || '—' },
                        { l: 'Group ID', v: detailData.applicationTracker?.groupId || '—' },
                        { l: 'Contract Type', v: detailData.applicationTracker?.contractType || '—' },
                        { l: 'Fee Schedule', v: detailData.applicationTracker?.feeSchedule || '—' },
                        { l: 'Est. Days', v: detailData.applicationTracker?.estimatedDays || '—' },
                        { l: 'Denial Reason', v: detailData.applicationTracker?.denialReason || '—' },
                      ].map((r, i) => (
                        <Grid item xs={6} sm={4} key={i}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{r.l}</Typography>
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, mt: 0.3 }}>{r.v}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ fontSize: 18, color: '#ec4899' }} /> Follow-Up History ({detailData.applicationTracker?.followUpDates?.length || 0})
                    </Typography>
                    {(!detailData.applicationTracker?.followUpDates || detailData.applicationTracker.followUpDates.length === 0) ? (
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>No follow-ups recorded</Typography>
                    ) : (
                      <List dense>
                        {detailData.applicationTracker.followUpDates.slice().reverse().map((fu, i) => (
                          <ListItem key={i} sx={{ bgcolor: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 2, mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}><Phone sx={{ fontSize: 18, color: '#ec4899' }} /></ListItemIcon>
                            <ListItemText
                              primary={<Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>{fu.notes}</Typography>}
                              secondary={`${fu.date ? new Date(fu.date).toLocaleDateString() : '—'} — ${fu.contactedBy || '—'}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Box>
              )}
              {detailTab === 4 && (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timeline sx={{ fontSize: 18, color: '#3b82f6' }} /> Activity Log
                  </Typography>
                  {(!detailData.activityLog || detailData.activityLog.length === 0) ? (
                    <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>No activity recorded</Typography>
                  ) : (
                    <List dense>
                      {detailData.activityLog.slice().reverse().map((act, i) => (
                        <ListItem key={i} sx={{ bgcolor: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 2, mb: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', mt: 0.8 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>{act.action}</Typography>}
                            secondary={
                              <Box>
                                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{act.details}</Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.3 }}>
                                  {act.date ? new Date(act.date).toLocaleString() : '—'} {act.performedBy ? `by ${fullName(act.performedBy)}` : ''}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* UPLOAD DOCUMENT DIALOG */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudUpload sx={{ color: '#8b5cf6' }} />
            <Typography sx={{ fontWeight: 700 }}>Upload Document</Typography>
          </Box>
          <IconButton onClick={() => setUploadOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {uploadChecklistIdx !== null && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>Uploading for checklist item #{uploadChecklistIdx + 1}</Alert>}
          <Box sx={{ border: '2px dashed', borderColor: '#e2e8f0', borderRadius: 3, p: 4, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: '#8b5cf6', bgcolor: alpha('#8b5cf6', 0.03) } }}
            onClick={() => fileRef.current?.click()}>
            <CloudUpload sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>Click to select file</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>PDF, images, documents up to 25MB</Typography>
            <input ref={fileRef} type="file" hidden onChange={handleUploadDoc} accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv" />
          </Box>
          {uploading && <LinearProgress sx={{ mt: 2, borderRadius: 2 }} />}
        </DialogContent>
      </Dialog>

      {/* PROVIDER DOCUMENTS DIALOG */}
      <Dialog open={provDocOpen} onClose={() => setProvDocOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FolderOpen sx={{ color: '#10b981' }} />
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Provider Documents</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{provDocProvider ? fullName(provDocProvider) : ''}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<CloudUpload />} onClick={() => provFileRef.current?.click()} sx={{ fontWeight: 600 }}>Upload</Button>
            <input ref={provFileRef} type="file" hidden onChange={handleProvDocUpload} accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv" />
            <IconButton onClick={() => setProvDocOpen(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {provDocUploading && <LinearProgress sx={{ mb: 2 }} />}
          {provDocs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FolderOpen sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
              <Typography color="text.secondary">No documents uploaded for this provider</Typography>
            </Box>
          ) : (
            <List>
              {provDocs.map((doc, i) => (
                <ListItem key={i} sx={{ bgcolor: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 2, mb: 0.5 }}>
                  <ListItemIcon><Description sx={{ color: '#3b82f6' }} /></ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{doc.name}</Typography>}
                    secondary={`${doc.fileSize ? `${(doc.fileSize / 1024).toFixed(0)} KB` : ''} | ${doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ''} | ${doc.type || 'other'}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => window.open(`${API_BASE}/documents/${doc._id}/download`, '_blank')} sx={{ color: '#3b82f6' }}><Download sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" onClick={async () => {
                      try { await documentsAPI.delete(doc._id); toast.success('Deleted'); const d = await credentialsAPI.getProviderDocuments(provDocProvider._id); setProvDocs(Array.isArray(d.data) ? d.data : []); } catch (err) { toast.error('Delete failed'); }
                    }} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* CAQH SCANNER DIALOG */}
      <Dialog open={scanOpen} onClose={() => { setScanOpen(false); setScanResult(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: 2 }}>
              <Scanner sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>CAQH Document Scanner</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Import provider data from CAQH Provider Summary</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => { setScanOpen(false); setScanResult(null); }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {!scanResult ? (
            <Box>
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                    {lbl('Select Provider *')}
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={<Checkbox size="small" checked={includeInactiveProviders} onChange={(e) => setIncludeInactiveProviders(e.target.checked)} />}
                      label={<Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Include inactive</Typography>}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    disabled={providersLoading}
                    value={scanProvider?._id || ''}
                    onChange={e => {
                      const id = e.target.value;
                      setScanProvider(providerOptions.find(p => String(p._id ?? p.id) === String(id)) || null);
                    }}>
                    <MenuItem value="">{providersLoading ? 'Loading providers…' : 'Select provider'}</MenuItem>
                    {providerOptions.map(p => (
                      <MenuItem key={p._id} value={p._id}>
                        {fullName(p)}{p.isGroupPractice ? ' (Group)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Tabs value={scanMode} onChange={(_, v) => setScanMode(v)} sx={{ mb: 2, '& .MuiTab-root': { fontSize: '0.82rem', fontWeight: 600, textTransform: 'none' } }}>
                <Tab value="file" label="Upload File" icon={<CloudUpload sx={{ fontSize: 18 }} />} iconPosition="start" />
                <Tab value="paste" label="Paste Text" icon={<ContentPaste sx={{ fontSize: 18 }} />} iconPosition="start" />
              </Tabs>
              {scanMode === 'file' ? (
                <Box sx={{ border: '2px dashed', borderColor: '#e2e8f0', borderRadius: 3, p: 4, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: '#8b5cf6', bgcolor: alpha('#8b5cf6', 0.03) } }}
                  onClick={() => scanFileRef.current?.click()}>
                  <Scanner sx={{ fontSize: 48, color: '#8b5cf6', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600 }}>Upload CAQH Provider Summary</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5 }}>
                    Supports .txt, .csv, .json files. Copy-paste the CAQH summary text into a .txt file.
                  </Typography>
                  <input ref={scanFileRef} type="file" hidden accept=".txt,.csv,.json,.pdf" />
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 1 }}>
                    Paste the CAQH Provider Summary text below. The scanner will extract fields like NPI, DEA, License, Board Cert, etc.
                  </Typography>
                  <TextField fullWidth multiline rows={12} value={scanText} onChange={e => setScanText(e.target.value)}
                    placeholder={`Paste CAQH Provider Summary here...\n\nExample format:\nFirst Name: John\nLast Name: Smith\nNPI Number: 1234567890\nState License: MD12345\nDEA Number: BS1234563\nBoard Certification: Internal Medicine\nSpecialty: Family Medicine\nMalpractice Carrier: NORCAL Group\n...`}
                    sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.82rem' } }} />
                </Box>
              )}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                  disabled={!scanProvider || scanning} onClick={handleScan}
                  sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700, px: 4 }}>
                  {scanning ? 'Scanning...' : 'Scan & Extract'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Scan Complete — {Object.keys(scanResult).filter(k => k !== 'rawData' && scanResult[k]).length} fields extracted</Typography>
                <Typography sx={{ fontSize: '0.78rem' }}>Review the extracted data below. Click "Apply to Provider" to save to the provider profile.</Typography>
              </Alert>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2, maxHeight: 400, overflow: 'auto' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Preview sx={{ fontSize: 18, color: '#8b5cf6' }} /> Extracted Data Preview
                </Typography>
                <Grid container spacing={1.5}>
                  {Object.entries(scanResult).filter(([k, v]) => k !== 'rawData' && v).map(([key, value], i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ display: 'flex', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'capitalize', minWidth: 140 }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#10b981' }}>{String(value)}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => setScanResult(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Back to Scanner</Button>
                <Button variant="contained" startIcon={<CheckCircle />} onClick={handleApplyScan}
                  sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700, px: 4 }}>
                  Apply to Provider Profile
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* FOLLOW-UP DIALOG */}
      <Dialog open={followUpOpen} onClose={() => setFollowUpOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone sx={{ color: '#ec4899' }} /> Record Follow-Up
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {lbl('Follow-Up Notes *')}
          <TextField fullWidth multiline rows={4} value={followUpNotes} onChange={e => setFollowUpNotes(e.target.value)}
            placeholder="Enter follow-up details... e.g. Called Aetna provider relations, spoke with Sarah, application still in review, follow up again in 2 weeks." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFollowUpOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleFollowUp} disabled={!followUpNotes.trim()}
            sx={{ background: 'linear-gradient(135deg, #ec4899, #db2777)', fontWeight: 700 }}>Save Follow-Up</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Application?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This will delete the application and all associated documents. This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Credentials;
